// Feedback widget — floating button + modal for user feedback submission
import { showToast, USER_KEY, authenticatedFetch } from '../shared/utils.ts'
import { t, applyTranslations } from '../shared/i18n.ts'
import { trackFeedbackSubmit } from '../shared/analytics.ts'
import { api_v1_feedback_path } from '../shared/api-routes'
import { showModal, hideModal } from '../shared/modal.ts'

/**
 * Initialize the feedback widget: creates the floating button and modal,
 * attaches event listeners. Should be called once on DOMContentLoaded.
 */
export function initFeedback() {
  createFeedbackButton()
  createFeedbackModal()
  attachEventListeners()
  // Update feedback button aria-label after translations are loaded
  const btn = document.getElementById('feedbackBtn')
  if (btn) {
    const label = t('feedback.button')
    if (label && label !== 'feedback.button') {
      btn.setAttribute('aria-label', label)
    }
  }
  applyTranslations()
}

function createFeedbackButton() {
  if (document.getElementById('feedbackBtn')) return

  const btn = document.createElement('button')
  btn.id = 'feedbackBtn'
  btn.className = 'feedback-btn'
  btn.setAttribute('aria-label', t('feedback.button'))
  btn.setAttribute('data-i18n', '[aria-label]feedback.button')
  btn.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
    <span class="feedback-btn-label" data-i18n="feedback.button">${t('feedback.button')}</span>
  `
  document.body.appendChild(btn)
}

function createFeedbackModal() {
  if (document.getElementById('feedbackModal')) return

  const overlay = document.createElement('div')
  overlay.id = 'feedbackModal'
  overlay.className = 'feedback-overlay'
  overlay.hidden = true

  overlay.innerHTML = `
    <div class="feedback-modal" role="dialog" aria-labelledby="feedbackTitle" aria-modal="true">
      <div class="feedback-header">
        <h3 id="feedbackTitle" data-i18n="feedback.title">${t('feedback.title')}</h3>
        <button class="feedback-close" id="feedbackClose" data-i18n="[aria-label]common.close" aria-label="Close">&times;</button>
      </div>
      <div class="feedback-body">
        <label for="feedbackType" data-i18n="feedback.typeLabel">${t('feedback.typeLabel')}</label>
        <select id="feedbackType" class="feedback-select">
          <option value="bug" data-i18n="feedback.typeBug">${t('feedback.typeBug')}</option>
          <option value="feature" data-i18n="feedback.typeFeature">${t('feedback.typeFeature')}</option>
          <option value="question" data-i18n="feedback.typeQuestion">${t('feedback.typeQuestion')}</option>
          <option value="general" selected data-i18n="feedback.typeGeneral">${t('feedback.typeGeneral')}</option>
        </select>
        <label for="feedbackMessage" data-i18n="feedback.messageLabel">${t('feedback.messageLabel')}</label>
        <textarea id="feedbackMessage" class="feedback-textarea" rows="5"
          placeholder="${t('feedback.messagePlaceholder')}"
          data-i18n="[placeholder]feedback.messagePlaceholder"
          required></textarea>
        <label for="feedbackEmail" data-i18n="feedback.emailLabel">${t('feedback.emailLabel')}</label>
        <input type="email" id="feedbackEmail" class="feedback-input"
          placeholder="${t('feedback.emailPlaceholder')}"
          data-i18n="[placeholder]feedback.emailPlaceholder">
      </div>
      <div class="feedback-footer">
        <button id="feedbackCancel" class="feedback-btn-cancel" data-i18n="feedback.cancel">${t('feedback.cancel')}</button>
        <button id="feedbackSubmit" class="feedback-btn-submit" data-i18n="feedback.submit">${t('feedback.submit')}</button>
      </div>
    </div>
  `

  document.body.appendChild(overlay)
}

function attachEventListeners() {
  const btn = document.getElementById('feedbackBtn')
  const overlay = document.getElementById('feedbackModal')
  const closeBtn = document.getElementById('feedbackClose')
  const cancelBtn = document.getElementById('feedbackCancel')
  const submitBtn = document.getElementById('feedbackSubmit')

  // The planner mounts the widget twice — the `planner` entrypoint calls
  // initFeedback() on DOMContentLoaded and planner/app.ts calls it again once
  // translations are in. Binding twice gave the Submit button two handlers, so
  // one click POSTed the feedback twice and filed two rows. The flag lives on
  // the overlay rather than in module scope so a fresh DOM re-binds normally.
  if (overlay?.dataset.feedbackBound === '1') return
  if (overlay) overlay.dataset.feedbackBound = '1'

  if (btn) {
    btn.addEventListener('click', () => openFeedbackModal())
  }

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (target.id === 'bannerFeedbackLink' || target.closest('#bannerFeedbackLink')) {
      e.preventDefault()
      openFeedbackModal()
    }
  })

  if (closeBtn) {
    closeBtn.addEventListener('click', () => closeFeedbackModal())
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', () => closeFeedbackModal())
  }

  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeFeedbackModal()
    })
  }

  if (submitBtn) {
    submitBtn.addEventListener('click', () => submitFeedback())
  }

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay && !overlay.hidden) {
      closeFeedbackModal()
    }
  })
}

function isSignedIn(): boolean {
  try {
    const user = JSON.parse(localStorage.getItem(USER_KEY) || '{}')
    return Boolean(user && user.email)
  } catch {
    return false
  }
}

function openFeedbackModal() {
  // Auto-fill email from user profile if logged in
  const emailInput = document.getElementById('feedbackEmail')
  if (emailInput && !(emailInput as HTMLInputElement).value) {
    try {
      const user = JSON.parse(localStorage.getItem(USER_KEY) || '{}')
      if (user && user.email) {
        (emailInput as HTMLInputElement).value = user.email
      }
    } catch {
      // ignore parse errors
    }
  }

  if (!showModal('feedbackModal')) return
  // Focus the message textarea
  const textarea = document.getElementById('feedbackMessage')
  if (textarea) textarea.focus()
}

function closeFeedbackModal() {
  hideModal('feedbackModal')
}

function resetForm() {
  const type = document.getElementById('feedbackType')
  const message = document.getElementById('feedbackMessage')
  const email = document.getElementById('feedbackEmail')

  if (type) (type as HTMLSelectElement).value = 'general'
  if (message) (message as HTMLTextAreaElement).value = ''
  if (email) (email as HTMLInputElement).value = ''
}

async function submitFeedback() {
  const type = document.getElementById('feedbackType')
  const message = document.getElementById('feedbackMessage')
  const email = document.getElementById('feedbackEmail')
  const submitBtn = document.getElementById('feedbackSubmit')

  if (!message || !(message as HTMLTextAreaElement).value.trim()) {
    showToast(t('feedback.messageRequired'), 'warning')
    return
  }

  // Anonymous submitters must leave an address — the API rejects them
  // otherwise (#1701). Catching it here gives a plain explanation instead of
  // a bare 400 the sender cannot act on.
  if (!isSignedIn() && !(email as HTMLInputElement | null)?.value.trim()) {
    showToast(t('feedback.emailRequiredWhenAnonymous'), 'warning')
    return
  }

  const body = {
    feedback_type: type ? (type as HTMLSelectElement).value : 'general',
    message: (message as HTMLTextAreaElement).value.trim(),
    page_url: window.location.href,
    metadata: {
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight
    }
  }

  if (email && (email as HTMLInputElement).value.trim()) {
    (body as any).email = (email as HTMLInputElement).value.trim()
  }

  // Disable button during submission
  if (submitBtn) {
    (submitBtn as HTMLButtonElement).disabled = true
    submitBtn.textContent = '...'
  }

  try {
    const response = await authenticatedFetch(api_v1_feedback_path(), {
      method: 'POST',
      body,
    })

    if (response.ok) {
      trackFeedbackSubmit()
      showToast(t('feedback.success'), 'success')
      resetForm()
      closeFeedbackModal()
    } else {
      const data = await response.json().catch(() => ({}))
      showToast(data.error || t('feedback.error'), 'error')
    }
  } catch {
    showToast(t('feedback.error'), 'error')
  } finally {
    if (submitBtn) {
      (submitBtn as HTMLButtonElement).disabled = false
      submitBtn.textContent = t('feedback.submit')
    }
  }
}
